import './AttachmentsUploadDropdown.scss';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faChevronDown, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslator } from '@Localization';
import { ClickAwayListener } from '@mui/base';
import { Button, Dropdown, IconButton, Menu, MenuButton, MenuItem } from '@mui/joy';
import { observer } from 'mobx-react-lite';
import { ReactElement, useState } from 'react';
import { S_ClipLoader } from '../../../../design/loaders/clipLoader/S_ClipLoader';
import { ConfirmationModal } from '../../../components/ConfirmationModal';
import { ProgressBar } from '../../../components/ProgressBar';
import { IAttachmentUploadProgressData } from '../attachmentUploadStore';

library.add(faTimes);

interface IProps {
  progressData: IAttachmentUploadProgressData[];
  showConfirmModal: boolean;
  onToggleConfirmModal: () => void;
  onCancelUpload: (attachmentId: string) => void;
  onCancelUploadConfirm: () => Promise<void>;
}

const MenuItemWithProgressBar = observer(
  (props: { attachment: IAttachmentUploadProgressData; onCancelUpload(attachmentId: string): void }): ReactElement => (
    <MenuItem
      key={props.attachment.id}
      className='attachment-single-item'
    >
      <div className='top'>
        <div className='name text-left'>{props.attachment.name}</div>
        <div className='current-progress text-right'>{props.attachment.progress}%</div>
      </div>
      <div className='bottom'>
        <ProgressBar
          success={props.attachment.isSuccess}
          value={props.attachment.progress}
        />
        {props.attachment.progress < 100 && (
          <IconButton
            size='sm'
            color='danger'
            onClick={(): void => {
              props.onCancelUpload(props.attachment.id);
            }}
          >
            <FontAwesomeIcon icon={faTimes} />
          </IconButton>
        )}
      </div>
    </MenuItem>
  )
);

export const AttachmentUploadDropdown = observer((props: IProps): ReactElement => {
  const { t } = useTranslator();
  const [isOpen, setIsOpen] = useState<boolean>(false);

  return (
    <>
      {props.progressData.length > 0 && (
        <ClickAwayListener
          onClickAway={() => {
            setIsOpen(false);
          }}
        >
          <div className='top-nav-item'>
            <Dropdown open={isOpen}>
              <MenuButton
                onClick={() => {
                  setIsOpen((x) => !x);
                }}
                slots={{ root: Button }}
                slotProps={{
                  root: {
                    variant: 'topbar',
                    startDecorator: (
                      <S_ClipLoader
                        loading={true}
                        size={24}
                        color='currentColor'
                      />
                    ),
                    endDecorator: <FontAwesomeIcon icon={faChevronDown} />,
                  },
                }}
              >
                <span>{t('attachments_upload')}</span>
              </MenuButton>

              <Menu className='no-hover attachments-upload-menu'>
                {props.progressData.map((attachment) => (
                  <MenuItemWithProgressBar
                    key={attachment.id}
                    attachment={attachment}
                    onCancelUpload={props.onCancelUpload}
                  />
                ))}
              </Menu>
            </Dropdown>
          </div>
        </ClickAwayListener>
      )}
      <ConfirmationModal
        showModal={props.showConfirmModal}
        onConfirm={props.onCancelUploadConfirm}
        onCancel={props.onToggleConfirmModal}
        confirmationHeader='cancel_attachment_upload'
        confirmationQuestion='attachment_upload_cancelation_confirmation_question'
      />
    </>
  );
});
